import React, { useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import api from '@/services/api'

/**
 * Editorial page — review queue for the ZiR0 Substack pipeline.
 *
 * Tabs:
 *   - Queue: drafts awaiting review (status=draft|failed_qc), approve/reject/schedule
 *   - Research: incoming research stories awaiting human approval
 *   - Calendar: weekly context editor (theme, meta_theme, friday_thesis)
 *   - Runtime: which env-flag features are flipped (dry-run vs live)
 */

type ArticleSummary = {
  id: string
  article_type: string
  vertical: string
  iso_week: string
  slot: string
  title: string
  word_count: number
  qc_passed: boolean
  regen_count: number
  status: string
  scheduled_publish_at: string | null
  published_at: string | null
  created_at: string
  updated_at: string
}

type ArticleDetail = ArticleSummary & {
  body_markdown: string
  scratchpad: string | null
  qc_report: any
  brief: any
  research_story_ids: string[] | null
  substack_post_id: string | null
}

type ResearchStory = {
  id: string
  source: string
  source_kind: string
  vertical: string
  url: string
  headline: string
  published_at: string
  relevance_score: number | null
  flag: string | null
  approved: boolean | null
  brief_summary: string | null
  key_data_point: string | null
  ziro_angle: string | null
}

type RuntimeInfo = {
  pipeline_enabled: boolean
  first_iso_week: string | null
  substack_publish_live: boolean
  substack_pub_url: string | null
  substack_dry_run_env: boolean
  brave_enabled: boolean
  perplexity_enabled: boolean
  gmail_enabled: boolean
  slack_enabled: boolean
  reviewer_configured: boolean
  max_regen_attempts: number
  research_top_n_per_vertical: number
}

type Tab = 'queue' | 'research' | 'calendar' | 'runtime'

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-blue-100 text-blue-800',
  failed_qc: 'bg-amber-100 text-amber-800',
  reviewing: 'bg-purple-100 text-purple-800',
  approved: 'bg-green-100 text-green-800',
  published: 'bg-emerald-200 text-emerald-900',
  published_dryrun: 'bg-slate-200 text-slate-800',
  rejected: 'bg-red-100 text-red-800',
  publish_failed: 'bg-red-200 text-red-900',
}

export default function EditorialPage() {
  const [tab, setTab] = useState<Tab>('queue')

  return (
    <div className="p-6">
      <div className="mb-4 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Editorial — ZiR0 Substack</h1>
          <p className="text-gray-500 text-sm">
            Review queue for daily generated drafts. 6-layer prompt stack, QC engine, regen loop.
          </p>
        </div>
        <RuntimeBadge />
      </div>

      <div className="mb-4 flex gap-2 border-b">
        {(['queue', 'research', 'calendar', 'runtime'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm capitalize ${
              tab === t ? 'border-b-2 border-blue-600 font-medium' : 'text-gray-500'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'queue' && <QueueTab />}
      {tab === 'research' && <ResearchTab />}
      {tab === 'calendar' && <CalendarTab />}
      {tab === 'runtime' && <RuntimeTab />}
    </div>
  )
}

function RuntimeBadge() {
  const [info, setInfo] = useState<RuntimeInfo | null>(null)
  useEffect(() => {
    api.get<RuntimeInfo>('/editorial/runtime').then((r) => setInfo(r.data)).catch(() => {})
  }, [])
  if (!info) return null
  const live = info.substack_publish_live
  return (
    <span className={`text-xs px-2 py-1 rounded ${live ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900'}`}>
      {live ? 'PUBLISH: live' : 'PUBLISH: dry-run'}
      {info.pipeline_enabled ? ' · pipeline ON' : ' · pipeline OFF'}
    </span>
  )
}

// ─── Queue Tab ────────────────────────────────────────────────────────────

function QueueTab() {
  const [articles, setArticles] = useState<ArticleSummary[]>([])
  const [selected, setSelected] = useState<ArticleDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('')

  const load = () => {
    setLoading(true)
    api
      .get<ArticleSummary[]>('/editorial/articles', { params: statusFilter ? { status: statusFilter } : {} })
      .then((r) => setArticles(r.data))
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    load()
  }, [statusFilter])

  const select = (id: string) => {
    api.get<ArticleDetail>(`/editorial/articles/${id}`).then((r) => setSelected(r.data))
  }

  const approve = (id: string) => {
    api.post(`/editorial/articles/${id}/approve`).then(() => {
      load()
      if (selected?.id === id) select(id)
    })
  }
  const reject = (id: string) => {
    api.post(`/editorial/articles/${id}/reject`).then(() => {
      load()
      if (selected?.id === id) select(id)
    })
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-5">
        <div className="mb-2 flex gap-2">
          <select
            className="border rounded px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="draft">draft</option>
            <option value="failed_qc">failed_qc</option>
            <option value="approved">approved</option>
            <option value="published">published</option>
            <option value="published_dryrun">published_dryrun</option>
            <option value="rejected">rejected</option>
          </select>
        </div>
        <div className="border rounded">
          {loading && <div className="p-4 text-sm text-gray-500">Loading…</div>}
          {!loading && articles.length === 0 && (
            <div className="p-4 text-sm text-gray-500">No drafts in queue.</div>
          )}
          {articles.map((a) => (
            <button
              key={a.id}
              onClick={() => select(a.id)}
              className={`w-full text-left border-b p-3 hover:bg-gray-50 ${selected?.id === a.id ? 'bg-blue-50' : ''}`}
            >
              <div className="flex justify-between items-baseline">
                <div className="font-medium text-sm">{a.title || '(untitled)'}</div>
                <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_BADGE[a.status] || ''}`}>{a.status}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {a.iso_week} · {a.slot} · {a.vertical} · {a.word_count}w
                {' · QC '}
                {a.qc_passed ? '✓' : '✗'}
                {a.regen_count > 0 && ` · regen ×${a.regen_count}`}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="col-span-7">
        {selected ? (
          <ArticleDetailView article={selected} onApprove={approve} onReject={reject} />
        ) : (
          <div className="text-sm text-gray-500">Select an article to review</div>
        )}
      </div>
    </div>
  )
}

function ArticleDetailView({
  article,
  onApprove,
  onReject,
}: {
  article: ArticleDetail
  onApprove: (id: string) => void
  onReject: (id: string) => void
}) {
  const checks = (article.qc_report?.checks || []) as any[]
  const [editTag, setEditTag] = useState<string | null>(null)
  const [tagNote, setTagNote] = useState('')

  const submitEditTag = (tagType: string) => {
    api.post(`/editorial/articles/${article.id}/edit-log`, {
      edit_type: tagType,
      notes: tagNote || null,
    }).then(() => {
      setEditTag(null)
      setTagNote('')
    })
  }

  return (
    <div>
      <div className="border-b pb-2 mb-3">
        <h2 className="text-lg font-semibold">{article.title}</h2>
        <div className="text-xs text-gray-500 mt-1">
          {article.article_type} · {article.iso_week} · {article.slot} · {article.vertical} ·{' '}
          {article.word_count} words
        </div>
      </div>

      <div className="mb-3 flex gap-2 flex-wrap">
        <button
          disabled={['approved', 'published', 'published_dryrun'].includes(article.status)}
          onClick={() => onApprove(article.id)}
          className="px-3 py-1.5 text-sm bg-green-600 text-white rounded disabled:bg-gray-300"
        >
          Approve
        </button>
        <button
          disabled={article.status === 'rejected'}
          onClick={() => onReject(article.id)}
          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded disabled:bg-gray-300"
        >
          Reject
        </button>
        <div className="relative inline-block">
          <button
            onClick={() => setEditTag(editTag ? null : 'OPENING')}
            className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded"
          >
            Tag edit ▾
          </button>
          {editTag && (
            <div className="absolute z-10 mt-1 bg-white border rounded shadow-lg p-3 w-72">
              <div className="text-xs text-gray-700 mb-2">Tag type:</div>
              <div className="flex flex-wrap gap-1 mb-2">
                {['STYLE', 'FACT', 'STRUCTURE', 'OPENING', 'CLOSING', 'ADD', 'CUT'].map((t) => (
                  <button
                    key={t}
                    onClick={() => setEditTag(t)}
                    className={`px-2 py-0.5 text-xs rounded ${editTag === t ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <textarea
                className="w-full border rounded px-2 py-1 text-xs"
                rows={2}
                placeholder="optional note"
                value={tagNote}
                onChange={(e) => setTagNote(e.target.value)}
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => submitEditTag(editTag)}
                  className="px-2 py-1 text-xs bg-purple-600 text-white rounded"
                >
                  Submit
                </button>
                <button onClick={() => setEditTag(null)} className="px-2 py-1 text-xs bg-gray-200 rounded">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mb-3 border rounded p-3 bg-gray-50">
        <div className="text-xs font-medium text-gray-700 mb-1">QC Report</div>
        <div className="text-xs">
          {checks.length === 0 ? (
            'no checks recorded'
          ) : (
            <ul>
              {checks.map((c: any, i: number) => (
                <li key={i} className={c.passed ? 'text-green-700' : 'text-red-700'}>
                  {c.passed ? '✓' : '✗'} {c.name} — {c.detail}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="border rounded p-4 bg-white max-h-[500px] overflow-y-auto prose prose-sm max-w-none">
        <ReactMarkdown>{article.body_markdown}</ReactMarkdown>
      </div>

      {article.scratchpad && (
        <details className="mt-3 border rounded p-3 bg-amber-50 text-xs">
          <summary className="cursor-pointer text-gray-700">Show writer scratchpad (move labels, opening candidates)</summary>
          <pre className="whitespace-pre-wrap font-mono text-xs mt-2">{article.scratchpad}</pre>
        </details>
      )}

      {article.brief && (
        <details className="mt-2 border rounded p-3 bg-gray-50 text-xs">
          <summary className="cursor-pointer text-gray-700">Show source brief (Layer 5)</summary>
          <pre className="whitespace-pre-wrap font-mono text-xs mt-2">{JSON.stringify(article.brief, null, 2)}</pre>
        </details>
      )}
    </div>
  )
}

// ─── Research Tab ─────────────────────────────────────────────────────────

function ResearchTab() {
  const [stories, setStories] = useState<ResearchStory[]>([])
  const [vertical, setVertical] = useState('')
  const [loading, setLoading] = useState(true)

  const load = () => {
    setLoading(true)
    const params: any = { approved: false }
    if (vertical) params.vertical = vertical
    api
      .get<ResearchStory[]>('/editorial/research-stories', { params })
      .then((r) => setStories(r.data))
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    load()
  }, [vertical])

  const approve = (id: string) => {
    api.post(`/editorial/research-stories/${id}/approve`).then(load)
  }

  return (
    <div>
      <div className="mb-2">
        <select
          className="border rounded px-2 py-1 text-sm"
          value={vertical}
          onChange={(e) => setVertical(e.target.value)}
        >
          <option value="">All verticals</option>
          <option value="finance_pe">finance_pe</option>
          <option value="legal">legal</option>
          <option value="education_workforce">education_workforce</option>
          <option value="cross_vertical">cross_vertical</option>
        </select>
      </div>
      {loading && <div className="text-sm text-gray-500">Loading…</div>}
      {!loading && stories.length === 0 && (
        <div className="text-sm text-gray-500">No pending research stories.</div>
      )}
      <div className="space-y-2">
        {stories.map((s) => (
          <div key={s.id} className="border rounded p-3">
            <div className="flex justify-between items-baseline">
              <a href={s.url} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline">
                {s.headline}
              </a>
              <span className="text-xs text-gray-500">{s.source} · {s.flag || '—'}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {s.vertical} · {s.source_kind} · score {s.relevance_score?.toFixed(2) ?? '—'} ·{' '}
              {new Date(s.published_at).toLocaleDateString()}
            </div>
            {s.brief_summary && <div className="mt-2 text-sm">{s.brief_summary}</div>}
            {s.ziro_angle && <div className="mt-1 text-xs italic text-gray-600">Angle: {s.ziro_angle}</div>}
            <div className="mt-2">
              <button
                onClick={() => approve(s.id)}
                className="px-2 py-1 text-xs bg-blue-600 text-white rounded"
              >
                Approve for use
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Calendar Tab ─────────────────────────────────────────────────────────

function CalendarTab() {
  const [iso, setIso] = useState(currentISOWeek())
  const [theme, setTheme] = useState('')
  const [metaTheme, setMetaTheme] = useState('')
  const [fridayThesis, setFridayThesis] = useState('')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    api
      .get(`/editorial/weekly-context/${iso}`)
      .then((r: any) => {
        setTheme(r.data?.theme || '')
        setMetaTheme(r.data?.meta_theme || '')
        setFridayThesis(r.data?.friday_thesis || '')
      })
      .catch(() => {})
  }, [iso])

  const save = () => {
    setSaving(true)
    setMsg('')
    api
      .post('/editorial/weekly-context', {
        iso_week: iso,
        theme,
        meta_theme: metaTheme || null,
        friday_thesis: fridayThesis || null,
      })
      .then(() => setMsg('saved'))
      .catch(() => setMsg('save failed'))
      .finally(() => setSaving(false))
  }

  return (
    <div className="space-y-3 max-w-2xl">
      <div>
        <label className="text-xs text-gray-700">ISO Week</label>
        <input
          className="border rounded px-2 py-1 ml-2 text-sm"
          value={iso}
          onChange={(e) => setIso(e.target.value)}
          placeholder="2026-W18"
        />
      </div>
      <div>
        <label className="text-xs text-gray-700">Theme</label>
        <input className="border rounded px-2 py-1 w-full text-sm" value={theme} onChange={(e) => setTheme(e.target.value)} />
      </div>
      <div>
        <label className="text-xs text-gray-700">Meta-theme</label>
        <input
          className="border rounded px-2 py-1 w-full text-sm"
          value={metaTheme}
          onChange={(e) => setMetaTheme(e.target.value)}
          placeholder="governance_gap | deployment_reality | structural_shift | regulatory"
        />
      </div>
      <div>
        <label className="text-xs text-gray-700">Friday thesis (one sentence)</label>
        <textarea
          className="border rounded px-2 py-1 w-full text-sm"
          rows={3}
          value={fridayThesis}
          onChange={(e) => setFridayThesis(e.target.value)}
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          disabled={saving}
          onClick={save}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded"
        >
          Save
        </button>
        {msg && <span className="text-xs text-gray-500">{msg}</span>}
      </div>
    </div>
  )
}

function currentISOWeek(): string {
  const d = new Date()
  const target = new Date(d.valueOf())
  const dayNr = (d.getUTCDay() + 6) % 7
  target.setUTCDate(target.getUTCDate() - dayNr + 3)
  const firstThursday = target.valueOf()
  target.setUTCMonth(0, 1)
  if (target.getUTCDay() !== 4) {
    target.setUTCMonth(0, 1 + ((4 - target.getUTCDay() + 7) % 7))
  }
  const week = 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000)
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`
}

// ─── Runtime Tab ──────────────────────────────────────────────────────────

function RuntimeTab() {
  const [info, setInfo] = useState<RuntimeInfo | null>(null)
  useEffect(() => {
    api.get<RuntimeInfo>('/editorial/runtime').then((r) => setInfo(r.data))
  }, [])
  if (!info) return <div className="text-sm text-gray-500">Loading…</div>

  const flags = useMemo(
    () => [
      { label: 'Pipeline enabled', value: info.pipeline_enabled, env: 'EDITORIAL_PIPELINE_ENABLED' },
      { label: 'Substack publish live', value: info.substack_publish_live, env: 'SUBSTACK_DRY_RUN + SUBSTACK_SESSION_TOKEN' },
      { label: 'Substack pub URL set', value: !!info.substack_pub_url, env: 'SUBSTACK_PUB_URL' },
      { label: 'Brave Search', value: info.brave_enabled, env: 'BRAVE_SEARCH_API_KEY' },
      { label: 'Perplexity (fallback)', value: info.perplexity_enabled, env: 'PERPLEXITY_API_KEY' },
      { label: 'Gmail newsletter parser', value: info.gmail_enabled, env: 'EDITORIAL_GMAIL_ACCOUNT' },
      { label: 'Slack notifications', value: info.slack_enabled, env: 'SLACK_EDITORIAL_WEBHOOK_URL' },
      { label: 'Reviewer configured', value: info.reviewer_configured, env: 'EDITORIAL_REVIEWER_USER_ID' },
    ],
    [info],
  )

  return (
    <div>
      <p className="text-sm text-gray-500 mb-3">
        Mirrors PLAN-ziro-substack-automation §17. Each flag flips when the listed env var lands.
      </p>
      <table className="w-full max-w-2xl border-collapse">
        <thead>
          <tr className="border-b text-left text-xs text-gray-700">
            <th className="py-2">Flag</th>
            <th>State</th>
            <th>Env var</th>
          </tr>
        </thead>
        <tbody>
          {flags.map((f) => (
            <tr key={f.label} className="border-b text-sm">
              <td className="py-2">{f.label}</td>
              <td>
                <span
                  className={`px-2 py-0.5 rounded text-xs ${
                    f.value ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900'
                  }`}
                >
                  {f.value ? 'live' : 'dry-run'}
                </span>
              </td>
              <td className="text-xs font-mono text-gray-600">{f.env}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 text-xs text-gray-600">
        First ISO week: <code>{info.first_iso_week || '—'}</code> · Max regen attempts:{' '}
        <code>{info.max_regen_attempts}</code> · Research top-N per vertical:{' '}
        <code>{info.research_top_n_per_vertical}</code>
      </div>
    </div>
  )
}
